import React, { ReactNode, useContext, useEffect, useState } from 'react'
import { join } from 'lodash'
import MaskedInput from 'react-maskedinput'
import styled, { css } from 'styled-components'
import Select from 'live/components/forms/ui/select'
import TriStateCheckbox from 'live/components/ui/TriStateCheckbox'
import i18n from 'live/services/i18n'
import {
    ModalField,
    CustomMeasureSelect,
    CustomDatePicker,
    ModalSectionHeader,
    InformationIconComponent,
    NumberInput
} from '../modal-components'
import authorization from 'live/services/authorization'
import { fetchEntityOptions, fetchSingleEntity, fetchUnitOptions, TUBULAR_API } from '../../api/api'
import { timestampToDateTime, dateTimeToTimestamp, dateAndTimeRegex, regexNumberAndComma } from '../../utils/parse'
import { RawActivityEvent, RawRunEvent, SelectOptions, Uom } from '../../utils/type'
import { STATIC_DATA_STATES, UnitsTypes } from '../chart-components'
import { ActivityErrors } from './types'
import { RunStatusOnActivity } from './RunStatusOnActivity'
import { StaticDataContext } from '../../context/StaticDataContext'

interface ActivityOptionsTemplate {
    tubularType: SelectOptions[]
    connectionType: SelectOptions[]
    restrictedConnectionType: SelectOptions[]
    restrictedPipeMovementType: SelectOptions[]
    restrictedPipeMovementSpeed: SelectOptions[]
    materialType: SelectOptions[]
}

interface ActivityTabProps {
    children?: ReactNode
    event: RawActivityEvent
    errors: ActivityErrors
    updateActivityEvent: (event: RawActivityEvent) => void
    runEvent: RawRunEvent
    onExtendLastTubularClick: () => void
    setSelectedTubularType: (tubularType?: SelectOptions) => void
    isPreviousCheckboxTrue: boolean
    setMDFinalOfPreviousTubular: number | null | undefined
    isFirstTubularByDepth: boolean
    showErrorFinalMDInput: boolean
    handleDelete: () => void
    setHasChanged: (x) => void
    clearErrors: () => void
}

const ActivityTab: React.FC<ActivityTabProps> = ({
    event,
    errors,
    updateActivityEvent,
    runEvent,
    onExtendLastTubularClick,
    setSelectedTubularType,
    isPreviousCheckboxTrue,
    setMDFinalOfPreviousTubular,
    isFirstTubularByDepth,
    showErrorFinalMDInput,
    handleDelete,
    setHasChanged,
    clearErrors
}: ActivityTabProps) => {
    // TODO: get last tubular --> end date, run(or tubular) if is by depth or time
    const eventStartDateTime = event ? timestampToDateTime(event.start) : null
    const eventEndDateTime = event && event.state == STATIC_DATA_STATES.FINISHED ? timestampToDateTime(event.end) : null

    const [selectedEvent, setSelectedEvent] = useState<RawActivityEvent>(event ?? {})
    const [tubularTypeList, setTubularTypeList] = useState<SelectOptions[]>([])
    const [connectionTypeList, setConnectionTypeList] = useState<SelectOptions[]>([])
    const [materialTypeList, setMaterialTypeList] = useState<SelectOptions[]>([])
    const [unitOptions, setUnitOptions] = useState<Uom[]>([])

    const [startDate, setStartDate] = useState(eventStartDateTime?.date || null)
    const [startHour, setStartHour] = useState(eventStartDateTime?.time || null)

    const [endDate, setEndDate] = useState(eventEndDateTime?.date || null)
    const [endHour, setEndHour] = useState(eventEndDateTime?.time || null)

    const { liveTheme } = useContext(StaticDataContext)

    const isConnectionTypeContainsTheValuesOfTubularType = [
        'CASING_RUNNING',
        'SCREEN_HANDLING',
        'TUBING_HANDLING'
    ].includes(selectedEvent.tubularType)
    const isMaterialTypeContainsTheValuesOfTubularType = ['CASING_RUNNING', 'TUBING_HANDLING'].includes(
        selectedEvent.tubularType
    )

    const showPermanentInstallationCheckbox =
        isPreviousCheckboxTrue &&
        (isConnectionTypeContainsTheValuesOfTubularType || selectedEvent.state !== STATIC_DATA_STATES.FINISHED)

    const fieldDisabledAfterEntityStart = selectedEvent.state !== STATIC_DATA_STATES.EMPTY

    const hasPermissionToEdit =
        authorization.superAdmin ||
        authorization.admin ||
        authorization.Permission.ADMIN ||
        authorization.Permission.OGPERFORMANCE_MANAGE_KPI ||
        authorization.Permission.OGPERFORMANCE_ADMINISTRATE_KPI

    useEffect(() => {
        if (dateAndTimeRegex.test(join([startDate, startHour], ' '))) {
            const timestamp = dateTimeToTimestamp(join([startDate, startHour], ' '))
            setSelectedEvent(prevstate => ({ ...prevstate, start: timestamp }))
        }
    }, [startDate, startHour])

    useEffect(() => {
        if (dateAndTimeRegex.test(join([endDate, endHour], ' '))) {
            const timestamp = dateTimeToTimestamp(join([endDate, endHour], ' '))
            setSelectedEvent(prevstate => ({ ...prevstate, end: timestamp }))
        }
    }, [endDate, endHour])

    useEffect(() => {
        fetchEntityOptions(TUBULAR_API).then((data: ActivityOptionsTemplate) => {
            const filteredConnectionTypes = data.connectionType.filter(
                connectionType => connectionType.value !== 'NOT_APPLICABLE'
            )
            const filteredMaterialTypes = data.materialType.filter(
                materialType => materialType.value !== 'NOT_APPLICABLE'
            )

            setTubularTypeList(data.tubularType)
            setConnectionTypeList(filteredConnectionTypes)
            setMaterialTypeList(filteredMaterialTypes)
        })
        fetchUnitOptions(UnitsTypes.LENGTH).then(r => {
            if (!r) return
            setUnitOptions(r.options)
            const defaultUnit = r.options.find(elem => elem.name == 'm' || r[0])

            runEvent?.tubularsIntervalPerTime
                ? setSelectedEvent(prevstate => ({
                      ...prevstate,
                      startMD: null,
                      finalMD: null
                  }))
                : setSelectedEvent(prevstate => ({
                      ...prevstate,
                      startMD: {
                          value:
                              runEvent.type !== 'RISER'
                                  ? setMDFinalOfPreviousTubular ?? 0
                                  : setMDFinalOfPreviousTubular ?? prevstate?.startMD?.value ?? null,
                          uom: prevstate?.startMD?.uom || defaultUnit
                      },
                      finalMD: {
                          value: prevstate?.finalMD?.value,
                          uom: prevstate?.finalMD?.uom || defaultUnit
                      }
                  }))
        })
    }, [])

    useEffect(() => {
        fetchUnitOptions(UnitsTypes.LENGTH).then(r => {
            if (!r) return
            setUnitOptions(r.options)
            const oDdefaultUnit = r.options.find(elem => elem.name == 'in')

            if (selectedEvent.tubularType !== 'CASING_RUNNING' && selectedEvent.tubularType !== 'DPR_RUNNING') {
                setSelectedEvent(prevstate => ({ ...prevstate, oD: null }))
            } else {
                setSelectedEvent(prevstate => ({
                    ...prevstate,
                    oD: {
                        value: prevstate.oD?.value ?? null,
                        uom: prevstate?.oD?.uom || oDdefaultUnit
                    }
                }))
            }

            setSelectedEvent(prevstate => ({
                ...prevstate,
                connectionType: !isConnectionTypeContainsTheValuesOfTubularType
                    ? 'NOT_APPLICABLE'
                    : prevstate.connectionType
                    ? 'NOT_APPLICABLE'
                    : '',
                materialType: !isMaterialTypeContainsTheValuesOfTubularType
                    ? 'NOT_APPLICABLE'
                    : prevstate.materialType
                    ? 'NOT_APPLICABLE'
                    : ''
            }))

            if (event.id) {
                fetchSingleEntity(TUBULAR_API, event.id).then(tubular => {
                    if (!tubular) return
                    const savedOdValue = tubular?.oD?.value
                    const savedConnectionType = tubular?.connectionType
                    const savedMaterialType = tubular?.materialType

                    if (selectedEvent.tubularType !== 'CASING_RUNNING' && selectedEvent.tubularType !== 'DPR_RUNNING') {
                        setSelectedEvent(prevstate => ({ ...prevstate, oD: null }))
                    } else {
                        setSelectedEvent(prevstate => ({
                            ...prevstate,
                            oD: {
                                value: savedOdValue ?? prevstate.oD?.value ?? null,
                                uom: prevstate?.oD?.uom || oDdefaultUnit
                            }
                        }))
                    }

                    setSelectedEvent(prevstate => ({
                        ...prevstate,
                        connectionType: !isConnectionTypeContainsTheValuesOfTubularType
                            ? 'NOT_APPLICABLE'
                            : savedConnectionType ?? prevstate.connectionType ?? '',
                        materialType: !isMaterialTypeContainsTheValuesOfTubularType
                            ? 'NOT_APPLICABLE'
                            : savedMaterialType ?? prevstate.materialType ?? ''
                    }))
                })
            }
        })
    }, [selectedEvent.tubularType])

    // required field
    useEffect(() => {
        if (startHour === '') {
            setSelectedEvent(prevstate => ({ ...prevstate, start: 0 }))
        }
        if (endHour === '') {
            setSelectedEvent(prevstate => ({ ...prevstate, end: 0 }))
        }
    }, [startHour, endHour])

    useEffect(() => {
        if (
            selectedEvent.permanentTubularInstallation === undefined ||
            !isConnectionTypeContainsTheValuesOfTubularType
        ) {
            setSelectedEvent(prevstate => ({ ...prevstate, permanentTubularInstallation: false }))
        }
    }, [selectedEvent.permanentTubularInstallation, isConnectionTypeContainsTheValuesOfTubularType])

    useEffect(() => {
        if (event?.tubularType && tubularTypeList.length > 0) {
            setSelectedTubularType({
                value: event.tubularType,
                acronym:
                    tubularTypeList?.find(t => t?.value === event.tubularType)?.acronym ||
                    event.tubularType ||
                    undefined
            })
        }
    }, [tubularTypeList])

    useEffect(() => {
        updateActivityEvent(selectedEvent)
    }, [selectedEvent])

    return (
        <>
            {' '}
            <RunStatusOnActivity runEvent={runEvent} tubularEvent={selectedEvent} />
            <ModalSectionHeader label={i18n('Attributes')} />
            {!runEvent?.tubularsIntervalPerTime && (
                <div style={{ display: 'flex', width: '100%' }}>
                    <CheckboxSection
                        style={{
                            width: '7%'
                        }}
                        liveTheme={liveTheme}
                    >
                        <input
                            disabled={!hasPermissionToEdit}
                            checked={!!runEvent?.extendLastTubular}
                            onChange={onExtendLastTubularClick}
                            type="checkbox"
                        />
                    </CheckboxSection>
                    <div style={{ width: '90%', color: 'var(--gray-800)' }}>
                        {i18n(
                            'Increases the length of the last tubular if the final bit depth in the current run is greater than expected.'
                        )}
                        {InformationIconComponent(
                            'When activating this option, the system will increase the length of the last tubular to match the final bit depth of the current run, adjusting the final measured depth of the tubular automatically. If this option is disabled, there will be no tubular classification after your maximum depth and the final measured depth of the run.'
                        )}
                    </div>
                </div>
            )}
            <ModalField
                label={'Tubular Type'}
                field={
                    <StyledSelectWrapper>
                        <Select
                            disabled={!hasPermissionToEdit}
                            searchable={true}
                            clearable={false}
                            options={tubularTypeList.sort((a, b) => a.label.localeCompare(b.label))}
                            placeholder={selectedEvent.tubularType ? `${selectedEvent.tubularType}` : i18n('Select...')}
                            value={selectedEvent.tubularType !== '' && selectedEvent.tubularType}
                            onChange={value => {
                                setHasChanged(true)
                                clearErrors()
                                setSelectedEvent(prevstate => ({ ...prevstate, tubularType: value }))
                                setSelectedTubularType({
                                    value: value,
                                    acronym:
                                        tubularTypeList?.find(t => t?.value === value)?.acronym || value || undefined
                                })
                                handleDelete()
                            }}
                            style={errors?.tubularType ? { borderColor: '#D9534F' } : {}}
                        />
                    </StyledSelectWrapper>
                }
            />
            <ModalField
                label={'Tubular Connection Type'}
                labelAditionalField={InformationIconComponent(
                    'Connection types for Screen, Tubing and Casing tubes. The other tubulars will not consider the possibility of connection type and default to the option Not applicable.'
                )}
                field={
                    <>
                        <Select
                            searchable={false}
                            clearable={false}
                            options={connectionTypeList}
                            disabled={!hasPermissionToEdit || !isConnectionTypeContainsTheValuesOfTubularType}
                            placeholder={
                                isConnectionTypeContainsTheValuesOfTubularType
                                    ? i18n('Select...')
                                    : i18n('Not Applicable')
                            }
                            value={selectedEvent.connectionType ?? ''}
                            onChange={(value: string) => {
                                setSelectedEvent(prevstate => ({ ...prevstate, connectionType: value }))
                            }}
                            style={errors?.connectionType ? { borderColor: '#D9534F' } : {}}
                        />
                        {showPermanentInstallationCheckbox && (
                            <TriStateCheckboxContainer liveTheme={liveTheme}>
                                <TriStateCheckbox
                                    className="tristate-checkbox"
                                    checked={
                                        isConnectionTypeContainsTheValuesOfTubularType &&
                                        selectedEvent.permanentTubularInstallation
                                    }
                                    disabled={
                                        !hasPermissionToEdit ||
                                        !isConnectionTypeContainsTheValuesOfTubularType ||
                                        fieldDisabledAfterEntityStart
                                    }
                                    onChange={() =>
                                        setSelectedEvent(prevstate => ({
                                            ...prevstate,
                                            permanentTubularInstallation: !selectedEvent.permanentTubularInstallation
                                        }))
                                    }
                                />
                                <TriStateCheckboxMessage>
                                    <div
                                        style={{
                                            color: !isConnectionTypeContainsTheValuesOfTubularType
                                                ? 'var(--gray-600)'
                                                : 'var(--gray-800)'
                                        }}
                                    >
                                        {i18n('Permanent Tubular Installation')}
                                    </div>
                                    {InformationIconComponent(
                                        'By checking this box, you are informing that the tubular will be installed in the well permanently and that it will not return to the surface.'
                                    )}
                                </TriStateCheckboxMessage>
                            </TriStateCheckboxContainer>
                        )}
                    </>
                }
            />
            <ModalField
                label={`Tubular OD`}
                labelAditionalField={
                    <>
                        <CustomMeasureWithTooltip>
                            <CustomMeasureSelect
                                // disabled={fieldDisabledAfterEntityStart}
                                disabled
                                setSelectedUomValue={uomValue =>
                                    setSelectedEvent(prevstate => ({ ...prevstate, oD: uomValue }))
                                }
                                selectedOptionValues={selectedEvent?.oD}
                                options={unitOptions}
                            />
                        </CustomMeasureWithTooltip>
                        {InformationIconComponent(
                            'You must fill this field with a value less than the Section OD value when the Tubular Type is Casing.'
                        )}
                    </>
                }
                field={
                    <NumberInput
                        value={
                            selectedEvent.tubularType === 'CASING_RUNNING' ||
                            selectedEvent.tubularType === 'DPR_RUNNING'
                                ? selectedEvent.oD?.value
                                : ''
                        }
                        disabled={
                            !hasPermissionToEdit ||
                            (selectedEvent.tubularType !== 'CASING_RUNNING' &&
                                selectedEvent.tubularType !== 'DPR_RUNNING')
                        }
                        onChange={e => {
                            if (regexNumberAndComma.test(e?.target?.value)) {
                                e.persist()
                                setSelectedEvent(prevstate => ({
                                    ...prevstate,
                                    oD: {
                                        value: Number(e?.target?.value),
                                        uom: selectedEvent?.oD?.uom
                                    }
                                }))
                            }
                            if (e?.target?.value === '') {
                                e.persist()
                                setSelectedEvent(prevstate => ({
                                    ...prevstate,
                                    oD: {
                                        uom: selectedEvent?.oD?.uom,
                                        value: null
                                    }
                                }))
                            }
                        }}
                        style={errors?.oD ? { borderColor: '#D9534F' } : {}}
                        placeholder={
                            selectedEvent.tubularType === 'CASING_RUNNING' ||
                            selectedEvent.tubularType === 'DPR_RUNNING'
                                ? i18n(`0.00`)
                                : i18n('Not Applicable')
                        }
                    />
                }
            />
            {runEvent?.tubularsIntervalPerTime && (
                <>
                    <ModalField
                        field={
                            <div className="input-date-time">
                                <ModalField
                                    label={'Start Date'}
                                    // labelAditionalField={InformationIconComponent(
                                    //     'This field is automatically filled with the End Date/Hour from the previous tubular.'
                                    // )}
                                    field={
                                        <CustomDatePicker
                                            isDisabled={!hasPermissionToEdit || fieldDisabledAfterEntityStart}
                                            date={startDate}
                                            setDate={setStartDate}
                                        />
                                    }
                                />
                                <ModalField
                                    label={'Start Hour'}
                                    field={
                                        <MaskedInput
                                            disabled={!hasPermissionToEdit || fieldDisabledAfterEntityStart}
                                            style={errors?.start ? { borderColor: '#D9534F' } : {}}
                                            mask="11:11"
                                            type="text"
                                            placeholder={'00:00'}
                                            value={startHour}
                                            // disabled={true}
                                            onChange={e => setStartHour(e.target.value)}
                                        />
                                    }
                                    // errorMessage={tubularErrors?.startHour}
                                />
                            </div>
                        }
                        enabled={false}
                    />
                    {selectedEvent.state != STATIC_DATA_STATES.EMPTY && (
                        <ModalField
                            field={
                                <div className="input-date-time">
                                    <ModalField
                                        label={'End Date'}
                                        field={
                                            <CustomDatePicker
                                                date={endDate}
                                                isDisabled={
                                                    !hasPermissionToEdit ||
                                                    selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                                }
                                                setDate={setEndDate}
                                            />
                                        }
                                    />
                                    <ModalField
                                        label={'End Hour'}
                                        field={
                                            <MaskedInput
                                                mask="11:11"
                                                type="text"
                                                placeholder={'00:00'}
                                                disabled={
                                                    !hasPermissionToEdit ||
                                                    selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                                }
                                                style={errors?.end ? { borderColor: '#D9534F' } : {}}
                                                value={endHour}
                                                onChange={e => setEndHour(e.target.value)}
                                            />
                                        }
                                    />
                                </div>
                            }
                        />
                    )}
                </>
            )}
            {!runEvent?.tubularsIntervalPerTime && (
                <div style={{ display: 'flex', gap: '17px' }}>
                    <ModalField
                        label={`Start MD`}
                        labelAditionalField={
                            <>
                                <CustomMeasureSelect
                                    disabled={!hasPermissionToEdit || fieldDisabledAfterEntityStart}
                                    setSelectedUomValue={uomValue =>
                                        setSelectedEvent(prevstate => ({ ...prevstate, startMD: uomValue }))
                                    }
                                    selectedOptionValues={selectedEvent?.startMD}
                                    options={unitOptions}
                                    //  disabled={event.finished}
                                />
                                {InformationIconComponent(
                                    'This field is automatically filled with the Final MD from the previous tubular.'
                                )}
                            </>
                        }
                        field={
                            <div className="input-normal">
                                <NumberInput
                                    disabled={
                                        !hasPermissionToEdit ||
                                        runEvent.type !== 'RISER' ||
                                        !isFirstTubularByDepth ||
                                        fieldDisabledAfterEntityStart
                                    }
                                    value={selectedEvent?.startMD?.value}
                                    style={errors?.startMD ? { borderColor: '#D9534F' } : {}}
                                    onChange={e => {
                                        if (regexNumberAndComma.test(e?.target?.value)) {
                                            e.persist()
                                            setSelectedEvent(prevstate => ({
                                                ...prevstate,
                                                startMD: {
                                                    uom: selectedEvent?.startMD?.uom,
                                                    value: Number(e?.target?.value)
                                                }
                                            }))
                                        }
                                        if (e?.target?.value === '') {
                                            e.persist()
                                            setSelectedEvent(prevstate => ({
                                                ...prevstate,
                                                startMD: {
                                                    uom: selectedEvent?.startMD?.uom,
                                                    value: null
                                                }
                                            }))
                                        }
                                    }}
                                    placeholder={runEvent.type === 'RISER' && isFirstTubularByDepth ? i18n('0.00') : ''}
                                />
                            </div>
                        }
                    />
                    <ModalField
                        label={`Final MD`}
                        labelAditionalField={
                            <>
                                <CustomMeasureSelect
                                    disabled={
                                        !hasPermissionToEdit || selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                    }
                                    options={unitOptions}
                                    setSelectedUomValue={uomValue =>
                                        setSelectedEvent(prevstate => ({ ...prevstate, finalMD: uomValue }))
                                    }
                                    selectedOptionValues={selectedEvent?.finalMD}
                                />
                                {InformationIconComponent(
                                    'You must fill this field with a value less than or equal to the Run Final MD value.'
                                )}
                            </>
                        }
                        field={
                            <div className="input-normal">
                                <NumberInput
                                    value={selectedEvent?.finalMD?.value}
                                    disabled={
                                        !hasPermissionToEdit || selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                    }
                                    onChange={e => {
                                        if (regexNumberAndComma.test(e?.target?.value)) {
                                            e.persist()
                                            setSelectedEvent(prevstate => ({
                                                ...prevstate,
                                                finalMD: {
                                                    uom: selectedEvent?.finalMD?.uom,
                                                    value: Number(e?.target?.value)
                                                }
                                            }))
                                        }
                                        if (e?.target?.value === '') {
                                            e.persist()
                                            setSelectedEvent(prevstate => ({
                                                ...prevstate,
                                                finalMD: {
                                                    uom: selectedEvent?.finalMD?.uom,
                                                    value: null
                                                }
                                            }))
                                        }
                                    }}
                                    style={errors?.finalMD || showErrorFinalMDInput ? { borderColor: '#D9534F' } : {}}
                                    placeholder={i18n('0.00')}
                                />
                                {showErrorFinalMDInput && (
                                    <div className="error-message-final-md">
                                        <i className="fas fa-exclamation-triangle" />
                                        <span>{i18n('This value is greater than to the Run Final MD')}</span>
                                    </div>
                                )}
                            </div>
                        }
                    />
                </div>
            )}
            <ModalField
                label={'Third Party Responsible'}
                field={
                    <input
                        disabled={!hasPermissionToEdit}
                        style={errors?.thirdPartyResponsible ? { borderColor: '#D9534F' } : {}}
                        placeholder={i18n('Description')}
                        type="text"
                        value={selectedEvent.thirdPartyResponsible || ''}
                        onChange={e => {
                            e.persist()
                            setSelectedEvent(prevstate => ({ ...prevstate, thirdPartyResponsible: e?.target?.value }))
                        }}
                    />
                }
            />
            <ModalField
                label={'Material Type'}
                field={
                    <>
                        <Select
                            searchable={false}
                            clearable={false}
                            disabled={!hasPermissionToEdit || !isMaterialTypeContainsTheValuesOfTubularType}
                            options={materialTypeList}
                            placeholder={
                                isMaterialTypeContainsTheValuesOfTubularType
                                    ? i18n('Select...')
                                    : i18n('Not Applicable')
                            }
                            value={selectedEvent.materialType ?? ''}
                            onChange={value => {
                                setSelectedEvent(prevstate => ({ ...prevstate, materialType: value }))
                            }}
                            style={errors?.materialType ? { borderColor: '#D9534F' } : {}}
                        />
                    </>
                }
            />
        </>
    )
}

const StyledSelectWrapper = styled.div`
    .Select-menu {
        max-height: 172px !important;
        overflow-y: auto;
    }
`

const CheckboxSection = styled.div<{ liveTheme?: string }>`
    display: 'flex';
    justify-content: center;
    flex-direction: column;

    input[type='checkbox']:not(.browser-default-input-style) {
        width: 14px;
        height: 14px;
        border-radius: 3px;
        border: 2px solid var(--gray-600);

        :checked {
            border-color: var(--blue-500);
            background-color: var(--blue-500);

            ::after {
                color: ${({ liveTheme }) => (liveTheme === 'dark' ? 'var(--gray-200)' : 'white')};
            }
        }
    }
`
const TriStateCheckboxContainer = styled.div<{ liveTheme?: string }>`
    display: flex;
    margin-top: 15px;

    input[type='checkbox']:not(.browser-default-input-style) {
        width: 14px;
        height: 14px;
        border-radius: 3px;
        border: 2px solid var(--gray-600);

        :checked {
            border-color: var(--blue-500);
            background-color: var(--blue-500);

            ::after {
                color: ${({ liveTheme }) => (liveTheme === 'dark' ? 'var(--gray-200)' : 'white')};
            }
        }
    }
`

const TriStateCheckboxMessage = styled.div`
    display: flex;
    font-style: normal;
    padding-left: 8px;
    line-height: 12px;
`

const CustomMeasureWithTooltip = styled.div`
    display: flex;
    align-items: center;
    margin-left: 0.25rem;
`
export default ActivityTab
